// frontend/src/pages/ConfiguracoesPage/ConfiguracoesPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout, Typography, Form, Input, Button, Card, Row, Col, Spin, Alert, Divider, App, Modal, Switch, Tabs } from 'antd';
import { UserOutlined, MailOutlined, BankOutlined, QrcodeOutlined, SaveOutlined, LockOutlined, PhoneOutlined, GoogleOutlined, CrownOutlined, IdcardOutlined, LoadingOutlined, DownloadOutlined, DeleteOutlined, SafetyOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import { getPerfil, updatePerfil } from '../../services/userService.js';
import { alterarSenha } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { validatePassword } from '../../utils/passwordValidation';
import { isValidCpfOrCnpj } from '../../utils/cardValidation';
import { useCepAutofill } from '../../hooks/useCepAutofill';
import { formatCepDisplay } from '../../utils/cepLookup';
import { exportarMeusDados, solicitarExclusaoConta, getMeuStatusLegal } from '../../services/legalService';
import { SUPORTE_EMAIL, getSuporteMailto } from '../../constants/support';
import { temModuloFinanceiro as checkModuloFinanceiro } from '../../utils/moduloAccess';
import { jaAtivouModoPrestador } from '../../utils/modoPrestador';
import TelegramVinculoCard from '../../components/TelegramVinculoCard/TelegramVinculoCard.jsx';
import CompartilharAcessoCard from '../../components/CompartilharAcessoCard/CompartilharAcessoCard.jsx';
import NotificacoesPreferenciasCard from '../../components/NotificacoesPreferenciasCard/NotificacoesPreferenciasCard.jsx';

const { Content } = Layout;
const { Title, Text } = Typography;

const dividerStyle = { borderColor: 'var(--color-border, #E8E2D6)', margin: '24px 0 16px 0' };

function ConfiguracoesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { message } = App.useApp();
    const { user, refreshUser, logout, temModuloFinanceiro } = useAuth();

    const [privacidadeEmail, setPrivacidadeEmail] = useState('privacidade@saldoativo.com.br');
    const [exportando, setExportando] = useState(false);
    const [exclusaoOpen, setExclusaoOpen] = useState(false);
    const [excluindo, setExcluindo] = useState(false);
    const [formExclusao] = Form.useForm();

    const [perfil, setPerfil] = useState({
        email: '', nome_completo: '', telefone: '', chave_pix_padrao: '', banco_preferencia: '',
        cpf: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [salvando, setSalvando] = useState(false);

    const [formPerfil] = Form.useForm();
    const [formPix] = Form.useForm();
    const [formAssinatura] = Form.useForm();
    const [formSenha] = Form.useForm();
    const { cepLoading, cepInputProps } = useCepAutofill(formAssinatura, message);

    // Estado simples para responsividade
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        getMeuStatusLegal().then((s) => {
            if (s?.privacidade_email) setPrivacidadeEmail(s.privacidade_email);
        }).catch(() => {});
    }, []);

    const fetchUserData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getPerfil();
            setPerfil(data);
            setError(null);
        } catch (err) {
            console.error("Falha ao buscar dados do usuário", err);
            setError(getErrorMessage(err, "Não foi possível carregar seus dados. Tente novamente mais tarde."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    const podeModoPrestador =
        (temModuloFinanceiro || checkModuloFinanceiro(user)) &&
        (jaAtivouModoPrestador(user) || jaAtivouModoPrestador(perfil));
    const nomeBloqueado = !!(perfil.cpf && String(perfil.cpf).trim());
    const cpfBloqueado = nomeBloqueado;
    const suporteMailto = getSuporteMailto({ emailConta: user?.email || perfil.email });

    const salvarPerfil = async (payload, sucesso) => {
        try {
            setSalvando(true);
            await updatePerfil(payload);
            message.success(sucesso);
            setPerfil((prev) => ({ ...prev, ...payload }));
            if (typeof refreshUser === 'function') {
                await refreshUser();
            }
        } catch (err) {
            console.error('Falha ao atualizar o perfil', err);
            message.error(getErrorMessage(err, 'Não foi possível salvar. Tente novamente.'));
        } finally {
            setSalvando(false);
        }
    };

    const handlePerfilSubmit = (values) => salvarPerfil({
        nome_completo: values.nome_completo,
        telefone: values.telefone?.trim() || null,
    }, 'Seu perfil foi atualizado.');

    const handlePixSubmit = (values) => {
        const payload = {
            chave_pix_padrao: values.chave_pix_padrao || null,
            banco_preferencia: values.banco_preferencia || null,
        };
        if (podeModoPrestador) {
            payload.modo_prestador_ativo = Boolean(values.modo_prestador_ativo);
            if (values.modo_prestador_ativo) {
                payload.preferencias_ui = { modo_prestador_ja_ativado: true };
            }
        }
        return salvarPerfil(payload, 'Dados de cobrança atualizados.');
    };

    const handleAssinaturaSubmit = (values) => {
        const cpfLimpo = values.cpf ? String(values.cpf).replace(/\D/g, '').slice(0, 14) : '';
        const estadoLimpo = values.estado ? String(values.estado).trim().slice(0, 2).toUpperCase() : null;
        return salvarPerfil({
            cpf: cpfLimpo || null,
            cep: values.cep?.trim() || null,
            endereco: values.endereco?.trim() || null,
            numero: values.numero?.trim() || null,
            complemento: values.complemento?.trim() || null,
            bairro: values.bairro?.trim() || null,
            cidade: values.cidade?.trim() || null,
            estado: estadoLimpo,
        }, 'Dados da assinatura atualizados.');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '80vh' }}>
                <Spin tip="Carregando Configurações..." size="large" />
            </div>
        );
    }

    if (error) {
        return <Alert message="Erro" description={error} type="error" showIcon style={{ margin: '1rem' }} />;
    }

    const cardProps = {
        bordered: !isMobile,
        bodyStyle: { padding: isMobile ? '16px' : '24px' },
    };

    const gridGutter = 16;

    const botaoSalvar = (
        <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
            <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                block={isMobile}
                size="large"
                loading={salvando}
                style={{ height: '45px', fontWeight: 500, minWidth: isMobile ? undefined : 220 }}
            >
                Salvar alterações
            </Button>
        </Form.Item>
    );

    const abaPerfil = (
        <Card {...cardProps}>
            <Form
                form={formPerfil}
                layout="vertical"
                onFinish={handlePerfilSubmit}
                initialValues={perfil}
                size={isMobile ? 'large' : 'middle'}
            >
                <Form.Item label="Email">
                    <Input prefix={<MailOutlined />} value={perfil.email} disabled style={{ backgroundColor: 'var(--color-surface-muted, #F8F4EC)', color: 'var(--color-text-secondary, #5A6A6A)' }} />
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                        O e-mail não pode ser alterado.
                    </Text>
                </Form.Item>

                <Row gutter={gridGutter}>
                    <Col xs={24} md={15}>
                        <Form.Item
                            name="nome_completo"
                            label="Nome Completo"
                            extra={nomeBloqueado ? (
                                <>
                                    Nome não pode ser alterado após o uso dos dados de cobrança. Para correção,{' '}
                                    <a href={suporteMailto}>contate o suporte</a>.
                                </>
                            ) : undefined}
                            rules={[{ required: true, message: 'Por favor, insira seu nome completo!' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Seu nome completo" disabled={nomeBloqueado} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={9}>
                        <Form.Item name="telefone" label="Telefone">
                            <Input prefix={<PhoneOutlined />} placeholder="(00) 00000-0000" maxLength={20} />
                        </Form.Item>
                    </Col>
                </Row>

                {botaoSalvar}
            </Form>
        </Card>
    );

    const abaCobranca = (
        <Card {...cardProps}>
            <Form
                form={formPix}
                layout="vertical"
                onFinish={handlePixSubmit}
                initialValues={perfil}
                size={isMobile ? 'large' : 'middle'}
            >
                <Alert
                    message="A chave Pix e o banco entram na mensagem do WhatsApp e no card público da cobrança."
                    type="info"
                    showIcon
                    style={{ marginBottom: 20, fontSize: '13px' }}
                />

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="chave_pix_padrao" label="Chave Pix Padrão" tooltip="CPF, E-mail, Telefone ou Aleatória.">
                            <Input prefix={<QrcodeOutlined />} placeholder="Sua chave Pix principal" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="banco_preferencia" label="Banco de Preferência" tooltip="Ex: Nubank, Inter (Opcional)">
                            <Input prefix={<BankOutlined />} placeholder="Nome do seu banco" />
                        </Form.Item>
                    </Col>
                </Row>

                {podeModoPrestador && (
                    <>
                        <Divider orientation="left" style={dividerStyle}>
                            <Text type="secondary" style={{ fontSize: '14px', fontWeight: 500 }}>Modo Prestador</Text>
                        </Divider>
                        <Alert
                            message="Clientes e A receber. Cobrança WhatsApp continua disponível com ou sem este modo. Cobrança ocasional de amigos não precisa disso."
                            type="info"
                            showIcon
                            style={{ marginBottom: 16, fontSize: '13px' }}
                        />
                        <Form.Item name="modo_prestador_ativo" label="Ferramentas de prestador" valuePropName="checked">
                            <Switch checkedChildren="On" unCheckedChildren="Off" />
                        </Form.Item>
                    </>
                )}

                {botaoSalvar}
            </Form>
        </Card>
    );

    const abaAssinatura = (
        <Card {...cardProps}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Gerencie sua assinatura: altere o plano, troque o cartão ou cancele.
            </Text>
            <Button type="default" icon={<CrownOutlined />} block={isMobile} style={{ minWidth: isMobile ? undefined : 220 }} onClick={() => navigate('/meu-plano')}>
                Gerenciar plano
            </Button>

            <Divider style={dividerStyle} />
            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '13px' }}>
                Dados usados no gateway de pagamento ao assinar ou alterar seu plano. Você também pode preenchê-los no checkout.
            </Text>

            <Form
                form={formAssinatura}
                layout="vertical"
                onFinish={handleAssinaturaSubmit}
                initialValues={{ ...perfil, cep: formatCepDisplay(perfil.cep || '') }}
                size={isMobile ? 'large' : 'middle'}
            >
                <Row gutter={gridGutter}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="cpf"
                            label="CPF ou CNPJ"
                            extra={cpfBloqueado ? (
                                <>
                                    CPF não pode ser alterado após o primeiro uso. Para correção,{' '}
                                    <a href={suporteMailto}>contate o suporte</a>.
                                </>
                            ) : undefined}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value || !String(value).replace(/\D/g, '')) return Promise.resolve();
                                        const digits = String(value).replace(/\D/g, '');
                                        if (digits.length !== 11 && digits.length !== 14) return Promise.reject(new Error('CPF (11 dígitos) ou CNPJ (14 dígitos)'));
                                        if (!isValidCpfOrCnpj(value)) return Promise.reject(new Error('CPF ou CNPJ inválido'));
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input prefix={<IdcardOutlined />} placeholder="Apenas números" maxLength={18} disabled={cpfBloqueado} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="cep" label="CEP">
                            <Input {...cepInputProps} suffix={cepLoading ? <LoadingOutlined spin /> : null} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="endereco" label="Endereço">
                    <Input placeholder="Rua, avenida" maxLength={255} />
                </Form.Item>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="numero" label="Número">
                            <Input placeholder="Nº" maxLength={20} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="complemento" label="Complemento">
                            <Input placeholder="Apto, bloco" maxLength={100} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="bairro" label="Bairro">
                            <Input maxLength={100} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Form.Item name="cidade" label="Cidade">
                            <Input maxLength={100} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={4}>
                        <Form.Item name="estado" label="UF">
                            <Input placeholder="SP" maxLength={2} />
                        </Form.Item>
                    </Col>
                </Row>

                {botaoSalvar}
            </Form>
        </Card>
    );

    const abaSeguranca = (
        <Card {...cardProps}>
            {user?.conta_vinculada_google && (
                <Alert
                    message="Conta vinculada ao Google"
                    description="Você entrou com Google. Pode definir uma senha do Saldo Ativo abaixo para também acessar por e-mail e senha."
                    type="info"
                    showIcon
                    icon={<GoogleOutlined />}
                    style={{ marginBottom: 20 }}
                />
            )}
            <Form
                layout="vertical"
                onFinish={async (values) => {
                    try {
                        const msg = validatePassword(values.nova_senha);
                        if (msg) {
                            message.error(msg);
                            return;
                        }
                        const exigeSenhaAtual = user?.tem_senha_definida && !user?.conta_vinculada_google;
                        const senhaAtual = exigeSenhaAtual ? values.senha_atual : '';
                        await alterarSenha(senhaAtual, values.nova_senha, values.confirmar_senha);
                        message.success(exigeSenhaAtual ? 'Senha alterada com sucesso.' : 'Senha definida com sucesso.');
                        formSenha.resetFields();
                        await refreshUser?.();
                    } catch (err) {
                        const exigeSenhaAtual = user?.tem_senha_definida && !user?.conta_vinculada_google;
                        message.error(getErrorMessage(err, exigeSenhaAtual ? 'Não foi possível alterar a senha. Verifique a senha atual.' : 'Não foi possível definir a senha.'));
                    }
                }}
                form={formSenha}
            >
                {user?.tem_senha_definida && !user?.conta_vinculada_google && (
                    <Form.Item name="senha_atual" label="Senha atual" rules={[{ required: true, message: 'Informe a senha atual' }]}>
                        <Input.Password prefix={<LockOutlined />} placeholder="Sua senha atual" />
                    </Form.Item>
                )}
                <Row gutter={gridGutter}>
                    <Col xs={24} sm={12}>
                        <Form.Item name="nova_senha" label={(user?.tem_senha_definida && !user?.conta_vinculada_google) ? 'Nova senha' : 'Senha do Saldo Ativo'} rules={[{ required: true, message: 'Informe a nova senha' }]}>
                            <Input.Password prefix={<LockOutlined />} placeholder="Mín. 8 caracteres" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="confirmar_senha"
                            label="Confirmar nova senha"
                            dependencies={['nova_senha']}
                            rules={[
                                { required: true, message: 'Confirme a nova senha' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('nova_senha') === value) return Promise.resolve();
                                        return Promise.reject(new Error('As senhas não coincidem'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Repita a nova senha" />
                        </Form.Item>
                    </Col>
                </Row>
                <Button type="primary" htmlType="submit" block={isMobile} style={{ minWidth: isMobile ? undefined : 220 }}>
                    {(user?.tem_senha_definida && !user?.conta_vinculada_google) ? 'Alterar senha' : 'Definir senha do Saldo Ativo'}
                </Button>
            </Form>
            {!user?.conta_vinculada_google && (
                <>
                    <Divider />
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        Vincular sua conta ao Google para fazer login com um clique estará disponível em breve.
                    </Text>
                </>
            )}
        </Card>
    );

    const abaIntegracoes = (
        <>
            {(temModuloFinanceiro || checkModuloFinanceiro(user)) && (
                <TelegramVinculoCard isMobile={isMobile} />
            )}
            <CompartilharAcessoCard isMobile={isMobile} />
        </>
    );

    const abaPrivacidade = (
        <>
            <Card title="Privacidade e dados" {...cardProps}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Exercite seus direitos previstos na LGPD. Dúvidas:{' '}
                    <a href={`mailto:${privacidadeEmail}`}>{privacidadeEmail}</a>
                </Text>
                <div style={{ marginBottom: 12 }}>
                    <SafetyOutlined style={{ marginRight: 8 }} />
                    <Link to="/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>
                    {' · '}
                    <Link to="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</Link>
                </div>
                <Divider style={{ margin: '16px 0' }} />
                <Button
                    icon={<DownloadOutlined />}
                    block
                    loading={exportando}
                    style={{ marginBottom: 8 }}
                    onClick={async () => {
                        setExportando(true);
                        try {
                            await exportarMeusDados();
                            message.success('Download iniciado.');
                        } catch (err) {
                            message.error(getErrorMessage(err, 'Não foi possível exportar os dados.'));
                        } finally {
                            setExportando(false);
                        }
                    }}
                >
                    Exportar meus dados (JSON)
                </Button>
                <Button danger icon={<DeleteOutlined />} block onClick={() => setExclusaoOpen(true)}>
                    Excluir minha conta
                </Button>
            </Card>

            <Card title="Suporte" {...cardProps} style={{ marginTop: 24 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Precisa de ajuda? Envie um e-mail para{' '}
                    <a href={suporteMailto}>{SUPORTE_EMAIL}</a>.
                </Text>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
                    No assunto, descreva o motivo. No corpo, explique o problema e anexe um print, se possível.
                </Text>
                <Button type="default" icon={<CustomerServiceOutlined />} block href={suporteMailto}>
                    Enviar e-mail de suporte
                </Button>
            </Card>
        </>
    );

    const items = [
        { key: 'perfil', label: 'Perfil', forceRender: true, children: abaPerfil },
        { key: 'cobranca', label: 'Cobrança & Pix', forceRender: true, children: abaCobranca },
        { key: 'assinatura', label: 'Assinatura', forceRender: true, children: abaAssinatura },
        { key: 'seguranca', label: 'Segurança', children: abaSeguranca },
        { key: 'notificacoes', label: 'Notificações', children: <NotificacoesPreferenciasCard isMobile={isMobile} /> },
        { key: 'integracoes', label: 'Integrações', children: abaIntegracoes },
        { key: 'privacidade', label: 'Privacidade', children: abaPrivacidade },
    ];

    // Links diretos para uma aba (ex.: Primeiros Passos → ?aba=integracoes).
    const abaPedida = searchParams.get('aba');
    const abaInicial = items.some((i) => i.key === abaPedida) ? abaPedida : 'perfil';

    return (
        <Content style={{ padding: isMobile ? '1rem' : '2rem', width: '100%' }}>
            <div style={{ marginBottom: isMobile ? '1.5rem' : '2rem' }}>
                <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>Configurações</Title>
                <Text type="secondary">Gerencie seus dados pessoais e preferências.</Text>
            </div>

            <div style={{ maxWidth: isMobile ? 720 : 1080 }}>
                <Tabs
                    className={isMobile ? 'sa-page-tabs' : 'sa-page-tabs sa-settings-tabs'}
                    type="card"
                    tabPosition={isMobile ? 'top' : 'left'}
                    size={isMobile ? 'large' : 'middle'}
                    defaultActiveKey={abaInicial}
                    items={items}
                />
            </div>

            <Modal
                title="Excluir conta permanentemente"
                open={exclusaoOpen}
                onCancel={() => { setExclusaoOpen(false); formExclusao.resetFields(); }}
                footer={null}
                destroyOnClose
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Esta ação é irreversível"
                    description="Seus dados no app serão removidos. Assinaturas ativas serão desativadas no gateway de pagamento."
                />
                <Form
                    form={formExclusao}
                    layout="vertical"
                    onFinish={async (values) => {
                        setExcluindo(true);
                        try {
                            await solicitarExclusaoConta({
                                senha: values.senha,
                                confirmacao: values.confirmacao,
                            });
                            message.success('Conta excluída.');
                            logout();
                            navigate('/login');
                        } catch (err) {
                            message.error(getErrorMessage(err, 'Não foi possível excluir a conta.'));
                        } finally {
                            setExcluindo(false);
                        }
                    }}
                >
                    {user?.tem_senha_definida && (
                        <Form.Item name="senha" label="Senha atual" rules={[{ required: true, message: 'Informe sua senha' }]}>
                            <Input.Password />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="confirmacao"
                        label='Digite EXCLUIR para confirmar'
                        rules={[{ required: true, message: 'Digite EXCLUIR' }]}
                    >
                        <Input placeholder="EXCLUIR" />
                    </Form.Item>
                    <Button type="primary" danger htmlType="submit" block loading={excluindo}>
                        Confirmar exclusão
                    </Button>
                </Form>
            </Modal>
        </Content>
    );
}

export default ConfiguracoesPage;
